import React from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { MoreHorizontal, X } from "lucide-react";
import { PurchaseDocStatus } from "types/InvoiceInbox";
import { getStatusLabelKey } from "utils/invoiceInboxStatus";
import { PILL_TONE_CLASS } from "./invoice-inbox-period-tree";
import { buttonClass, criticalButtonClass, primaryButtonClass } from "./invoice-doc-ui";

// The register's pills keep a rejected document as quiet as a new one; in the
// document's own header it is the status the reader must not miss.
const HEADER_PILL_CLASS = { ...PILL_TONE_CLASS, rejected: "bg-red-50 text-red-700" };

export type HeaderAction = {
  key: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Removing-type actions: listed last in the menu, in red. */
  critical?: boolean;
};

type Props = {
  docNumber: number | null;
  supplierName: string;
  status: PurchaseDocStatus;
  /** The one next step, when the reader is the one who can take it. */
  primary: HeaderAction | null;
  /** Shown beside the primary action, never inside the menu. */
  reject: HeaderAction | null;
  menu: HeaderAction[];
  onClose: () => void;
};

/** The document's sticky top bar: which document, where it stands, and what
 * to do with it — one main action in view, everything else behind "⋯". */
export default function InvoiceDocHeader({ docNumber, supplierName, status, primary, reject, menu, onClose }: Props) {
  const { t } = useTranslation();
  const ordinary = menu.filter((item) => !item.critical);
  const critical = menu.filter((item) => item.critical);

  return (
    // On a phone the bar is two rows: which document, "⋯" and close on top, the
    // one main action (and Reject) full-width underneath. From md up it is a single row.
    <div className="flex flex-wrap items-center gap-2 px-4 py-2 md:h-14 md:flex-nowrap md:py-0">
      <div className="flex min-w-0 flex-1 items-center gap-3 md:pr-1">
        <span className="font-semibold text-gray-900">#{docNumber}</span>
        {supplierName && <span className="min-w-0 truncate text-sm text-gray-500">{supplierName}</span>}
        <span className={clsx("shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium", HEADER_PILL_CLASS[status])}>
          {t(getStatusLabelKey(status), { defaultValue: status })}
        </span>
      </div>

      {(reject || primary) && (
        <div className="flex w-full gap-2 max-md:order-last max-md:gap-4 md:w-auto md:shrink-0">
          {reject && (
            <button
              type="button"
              className={clsx(criticalButtonClass, "max-md:flex-1")}
              disabled={reject.disabled}
              onClick={reject.onClick}
            >
              {reject.label}
            </button>
          )}
          {primary && (
            <button
              type="button"
              className={clsx(primaryButtonClass, "max-md:flex-1")}
              disabled={primary.disabled}
              onClick={primary.onClick}
            >
              {primary.label}
            </button>
          )}
        </div>
      )}
      {menu.length > 0 && (
        <Menu>
          <MenuButton
            className={clsx(buttonClass, "px-2 max-md:min-w-11")}
            aria-label={t("invoice_inbox_more_actions", { defaultValue: "More actions" })}
            title={t("invoice_inbox_more_actions", { defaultValue: "More actions" })}
          >
            <MoreHorizontal className="h-5 w-5" aria-hidden />
          </MenuButton>
          <MenuItems
            anchor="bottom end"
            modal={false}
            className="z-50 w-64 rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg outline-none [--anchor-gap:6px]"
          >
            {ordinary.map((item) => (
              <ActionMenuItem key={item.key} item={item} />
            ))}
            {ordinary.length > 0 && critical.length > 0 && <div className="my-1 border-t border-gray-100" />}
            {critical.map((item) => (
              <ActionMenuItem key={item.key} item={item} />
            ))}
          </MenuItems>
        </Menu>
      )}
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 max-md:p-3"
        aria-label={t("close", { defaultValue: "Close" })}
        title={t("close", { defaultValue: "Close" })}
      >
        <X className="h-5 w-5" aria-hidden />
      </button>
    </div>
  );
}

function ActionMenuItem({ item }: { item: HeaderAction }) {
  return (
    <MenuItem disabled={item.disabled}>
      <button
        type="button"
        onClick={item.onClick}
        className={clsx(
          "block w-full px-4 py-2 text-left data-[focus]:bg-gray-50 data-[disabled]:opacity-50 max-md:py-3",
          item.critical ? "text-red-600" : "text-gray-800"
        )}
      >
        {item.label}
      </button>
    </MenuItem>
  );
}
